import { useEffect, useRef } from "react";

/**
 * Atualiza uma lista sozinha, em silêncio, para vários terminais ficarem na mesma página.
 *
 * - Chama `fn` a cada `intervalMs` enquanto `enabled` for true e a aba estiver visível.
 * - Ao voltar para a aba/janela, atualiza na hora.
 * - Nunca sobrepõe chamadas: se a anterior ainda não terminou, pula o ciclo.
 * - `fn` deve ser silenciosa (sem spinner e sem toast de erro).
 */
export default function useLivePolling(fn, { intervalMs = 2000, enabled = true } = {}) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return undefined;
    let running = false;
    const tickNow = async () => {
      if (running || document.visibilityState === "hidden") return;
      running = true;
      try { await fnRef.current(); } catch { /* silencioso: tenta no próximo ciclo */ } finally { running = false; }
    };
    const id = setInterval(tickNow, intervalMs);
    const onVisible = () => { if (document.visibilityState === "visible") tickNow(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [intervalMs, enabled]);
}
